import React from 'react'
import * as Yup from 'yup'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    DialogProps,
    MenuItem,
    MenuItemProps,
} from '@mui/material'

import { useParams } from 'react-router-dom'
import { Form } from 'react-final-form'
import { TextField, makeValidate } from 'mui-rff'

import { filterMaybe, useSearchParams } from '@edge/spark/utils'
import {
    normalizeDashboards,
    useEditableDashboardsQuery,
    useCopyDashboardCardMutation,
} from '@edge/spark/io-dashboards'
import DashboardSelect from './DashboardSelect'
import { UserSelectAutocomplete } from '../../../UserSelectAutocomplete'
import UserDashboardSelect from './UserDashboardSelect'

interface FormDialogProps extends DialogProps {
    onClose: () => void
    widgetId: string
}

const FormDialog: React.FC<FormDialogProps> = ({ onClose, open, widgetId }: FormDialogProps) => {
    const { uid: priorUserId } = useSearchParams()
    const { dashId: priorDashId } = useParams<{ dashId?: string }>()
    const { data, loading: queryLoading } = useEditableDashboardsQuery({
        returnPartialData: true,
    })
    const [copyDash, { loading: mutationLoading }] = useCopyDashboardCardMutation()

    const usersFromAdminQuery = data?.administrator?.users?.filter(filterMaybe) || []
    const ownDashboards = data?.user?.dashboards?.filter(filterMaybe) || []
    const normalizedOwnDashboards = normalizeDashboards(ownDashboards)

    const schema = Yup.object({
        heading: Yup.string().required(),
        userId: Yup.string().test(
            'userId',
            // eslint-disable-next-line no-template-curly-in-string
            '${path} is required',
            (val) => !!val || !usersFromAdminQuery.length,
        ),
        dashId: Yup.string().required(),
    })
    const validate = makeValidate(schema)

    type Value = Yup.InferType<typeof schema>

    return (
        <Dialog open={open} onClose={onClose}>
            <Form<Value>
                validate={validate}
                onSubmit={(values): void => {
                    copyDash({
                        variables: {
                            priorUserId,
                            priorDashId,
                            widgetId,
                            ...values,
                        },
                    })
                    onClose()
                }}
                render={({ handleSubmit, invalid, values }): React.ReactNode => (
                    <form onSubmit={handleSubmit}>
                        <DialogTitle>Copy Card to Dashboard</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Copy the card to one of your dashboards or another user&apos;s.
                            </DialogContentText>
                            <TextField
                                label="New Card Heading"
                                required
                                name="heading"
                                autoFocus
                                fullWidth
                                margin="normal"
                            />
                            {usersFromAdminQuery.length ? (
                                <UserSelectAutocomplete loading={queryLoading} users={usersFromAdminQuery} />
                            ) : undefined}
                            {usersFromAdminQuery.length ? (
                                values.userId && <UserDashboardSelect userId={values.userId} />
                            ) : (
                                <DashboardSelect loading={queryLoading} dashboards={normalizedOwnDashboards} />
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="primary">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={invalid || mutationLoading}
                                color="primary"
                                variant="contained"
                            >
                                Save
                            </Button>
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

interface Props extends MenuItemProps {
    widgetId: string
}

export const CopyCardToDashMenuItem: React.FC<Props> = ({ widgetId, children, onClick, ...rest }: Props) => {
    const [open, setOpen] = React.useState(false)

    const handleClose = (): void => {
        setOpen(false)
    }

    const handleClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>): void => {
        onClick?.(event)
        setOpen(true)
    }

    return (
        <>
            <MenuItem {...rest} onClick={handleClick}>
                {children}
            </MenuItem>
            <FormDialog keepMounted open={open} widgetId={widgetId} onClose={handleClose} />
        </>
    )
}
